/**
 * NotificationCenter Component
 * 
 * Unified notification center that integrates with the Communications Hub.
 * Shows bell icon with dropdown for recent notifications.
 * Supports marking as read, archiving, and navigation to related pages.
 * Mobile-optimized with bottom sheet drawer.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  X, 
  CheckCircle2, 
  AlertCircle, 
  Info,
  Clock,
  ChevronRight,
  Settings,
  Loader2,
  Archive,
  Check,
  ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle,
  SheetClose
} from '../ui/sheet';
import { useNotifications } from '../../hooks/useNotifications';
import { useIsMobile } from '../../hooks/use-mobile';
import { cn } from '../../lib/utils';

interface NotificationCenterProps {
  className?: string;
  showSettings?: boolean;
  onSettingsClick?: () => void;
}

/**
 * NotificationCenter Component
 * 
 * Bell icon with dropdown showing recent notifications.
 * Integrates with Communications Hub when enabled, falls back to local storage.
 */
export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  className,
  showSettings = true,
  onSettingsClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    hubEnabled
  } = useNotifications({ limit: 20 });

  // Get icon for notification severity
  const getIcon = (severity: string) => {
    switch (severity) {
      case 'success':
        return <CheckCircle2 className="h-5 w-5 text-success" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-destructive" />;
      case 'warning':
        return <Clock className="h-5 w-5 text-warning" />;
      default:
        return <Info className="h-5 w-5 text-primary" />;
    }
  };

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Handle notification click - mark as read
  const handleNotificationClick = async (notification: typeof notifications[0]) => {
    if (!notification.readAt) {
      await markAsRead(notification.id);
    }
  };

  // Handle action button click
  const handleActionClick = async (e: React.MouseEvent, notification: typeof notifications[0]) => {
    e.stopPropagation();
    
    if (!notification.readAt) {
      await markAsRead(notification.id);
    }
    
    const actionUrl = notification.actionUrl || notification.actions?.[0]?.url;
    if (actionUrl) {
      setIsOpen(false);
      // Check if it's an internal or external URL
      if (actionUrl.startsWith('/')) {
        navigate(actionUrl);
      } else {
        window.open(actionUrl, '_blank');
      }
    }
  };

  // Handle archive
  const handleArchive = async (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    await archiveNotification(notificationId);
  };

  // Notification item component
  const NotificationItem = ({ notification }: { notification: typeof notifications[0] }) => {
    const isUnread = !notification.readAt;
    const actionUrl = notification.actionUrl || notification.actions?.[0]?.url;
    const actionLabel = notification.actions?.[0]?.label || 'View';
    const isExternal = actionUrl && !actionUrl.startsWith('/');
    
    return (
      <div
        className={cn(
          "p-4 hover:bg-muted/50 transition-colors cursor-pointer group min-h-[72px]",
          isUnread && "bg-primary/5"
        )}
        onClick={() => handleNotificationClick(notification)}
      >
        <div className="flex gap-3">
          <div className="flex-shrink-0 mt-0.5">
            {getIcon(notification.severity)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className={cn(
                "text-sm",
                isUnread && "font-medium"
              )}>
                {notification.title}
              </p>
              <div className="flex items-center gap-1 flex-shrink-0">
                {isUnread && (
                  <span className="h-2 w-2 bg-primary rounded-full" />
                )}
                <button
                  onClick={(e) => handleArchive(e, notification.id)}
                  className={cn(
                    "p-1.5 hover:bg-muted rounded transition-opacity min-h-[28px] min-w-[28px] flex items-center justify-center",
                    isMobile ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                  )}
                  title="Archive"
                >
                  <Archive className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            </div>
            {notification.body && (
              <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                {notification.body}
              </p>
            )}
            <div className="flex items-center justify-between mt-3 gap-2">
              <span className="text-xs text-muted-foreground">
                {formatTimeAgo(notification.createdAt)}
              </span>
              {actionUrl && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-7 px-3 text-xs gap-1.5"
                  onClick={(e) => handleActionClick(e, notification)}
                >
                  {actionLabel}
                  {isExternal ? (
                    <ExternalLink className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Notification list content
  const NotificationsList = () => (
    <div className="flex-1 overflow-y-auto">
      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
          <p className="text-sm text-muted-foreground mt-2">Loading...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="p-8 text-center">
          <Bell className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">No notifications</p>
          <p className="text-sm text-muted-foreground mt-1">You're all caught up!</p>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {notifications.map(notification => (
            <NotificationItem key={notification.id} notification={notification} />
          ))}
        </div>
      )}
    </div>
  );

  // Header actions
  const HeaderActions = () => (
    <div className="flex items-center gap-2">
      {unreadCount > 0 && (
        <Button 
          variant="ghost" 
          size="sm"
          onClick={markAllAsRead}
          className="text-xs h-8 px-3"
        >
          <Check className="h-3 w-3 mr-1.5" />
          Mark all read
        </Button>
      )}
      {showSettings && onSettingsClick && (
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => {
            setIsOpen(false);
            onSettingsClick();
          }}
          className="h-8 w-8 p-0"
        >
          <Settings className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  // Mobile: Use Sheet (bottom drawer)
  if (isMobile) {
    return (
      <div className={cn("relative", className)}>
        {/* Bell Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsOpen(true)}
          className="relative min-h-[44px] min-w-[44px]"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>

        {/* Mobile Sheet */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetContent 
            side="bottom" 
            className="h-[85vh] flex flex-col p-0 rounded-t-xl"
          >
            {/* Handle indicator */}
            <div className="flex justify-center py-2">
              <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
            </div>
            
            {/* Header */}
            <SheetHeader className="px-4 pb-3 border-b border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SheetTitle className="text-lg">Notifications</SheetTitle>
                  {hubEnabled && (
                    <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                      Hub
                    </span>
                  )}
                </div>
                <HeaderActions />
              </div>
            </SheetHeader>

            {/* Error State */}
            {error && (
              <div className="px-4 py-3 bg-destructive/10 border-b border-destructive/20">
                <p className="text-xs text-destructive">{error}</p>
              </div>
            )}

            {/* Notifications List */}
            <NotificationsList />

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-3 border-t border-border bg-background">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full min-h-[44px]"
                  onClick={() => {
                    setIsOpen(false);
                    if (onSettingsClick) {
                      onSettingsClick();
                    }
                  }}
                >
                  View all notifications
                </Button>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // Desktop: Use dropdown
  return (
    <div className={cn("relative", className)}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="absolute right-0 top-full mt-2 w-96 bg-background border border-border rounded shadow-md z-50 overflow-hidden max-h-[calc(100vh-100px)] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-foreground">Notifications</h3>
                {hubEnabled && (
                  <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                    Hub
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <HeaderActions />
                <button 
                  onClick={() => setIsOpen(false)} 
                  className="p-1.5 hover:bg-muted rounded ml-1"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            </div>

            {/* Error State */}
            {error && (
              <div className="p-3 bg-destructive/10 border-b border-destructive/20">
                <p className="text-xs text-destructive">{error}</p>
              </div>
            )}

            {/* Notifications List */}
            <div className="max-h-[400px] overflow-y-auto">
              <NotificationsList />
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="p-2 border-t border-border">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full text-xs"
                  onClick={() => {
                    setIsOpen(false);
                    if (onSettingsClick) {
                      onSettingsClick();
                    }
                  }}
                >
                  View all notifications
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;
